import { useWatch } from "react-hook-form";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { DataTable } from "@/components/admin/data-table";
import { FormToolbar, SimpleForm } from "@/components/admin/simple-form";
import { OwnerToolbar } from "./OwnerLock";
import { FreightTabs } from "./FreightTabs";
import { TextInput } from "@/components/admin/text-input";
import { NumberInput } from "@/components/admin/number-input";
import { NumberField } from "@/components/admin/number-field";
import { DateInput } from "@/components/admin/date-input";
import { DateField } from "@/components/admin/date-field";
import { SelectInput } from "@/components/admin/select-input";
import { SelectField } from "@/components/admin/select-field";
import {
  CompanyField,
  CompanyInput,
  LaneField,
  LaneInput,
  ListActions,
  OwnerField,
  OwnerInput,
  money,
} from "./common";

export const loadStatuses = [
  ["quoted", "Quoted"],
  ["booked", "Booked"],
  ["dispatched", "Dispatched"],
  ["in_transit", "In transit"],
  ["delivered", "Delivered"],
  ["invoiced", "Invoiced"],
  ["paid", "Paid"],
  ["cancelled", "Cancelled"],
].map(([id, name]) => ({ id, name }));
const filters = [
  <TextInput key="n" source="load_number@ilike" label="Load #" alwaysOn />,
  <SelectInput
    key="s"
    source="status"
    choices={loadStatuses}
    label="Status"
    alwaysOn
  />,
];

const MarginPreview = () => {
  const [c, k] = useWatch({ name: ["customer_rate", "carrier_rate"] });
  const m = (Number(c) || 0) - (Number(k) || 0);
  const pct = Number(c) ? ` (${((m / Number(c)) * 100).toFixed(1)}%)` : "";
  return (
    <div className="text-sm">
      <span className="font-medium">Margin: </span>
      {m.toLocaleString("en-US", money)}
      {pct}
    </div>
  );
};

const LoadForm = () => (
  <SimpleForm
    defaultValues={{ status: "booked" }}
    toolbar={
      <OwnerToolbar>
        <FormToolbar />
      </OwnerToolbar>
    }
  >
    <div className="grid grid-cols-2 gap-4 w-full">
      <TextInput source="load_number" label="Landstar load #" />
      <SelectInput source="status" choices={loadStatuses} label="Status" />
      <CompanyInput />
      <LaneInput />
      <TextInput source="carrier_name" label="Carrier name" />
      <TextInput source="carrier_mc" label="Carrier MC#" />
      <DateInput source="pickup_date" label="Pickup date" />
      <DateInput source="delivery_date" label="Delivery date" />
      <NumberInput source="customer_rate" label="Customer rate ($)" />
      <NumberInput source="carrier_rate" label="Carrier rate ($)" />
    </div>
    <MarginPreview />
    <OwnerInput />
    <TextInput source="notes" label="Notes" multiline />
  </SimpleForm>
);

// margin is a generated column; never send it back
const transform = ({ margin: _m, fts: _f, ...d }: any) => d;

export const LoadList = () => (
  <List
    actions={<ListActions />}
    filters={filters}
    sort={{ field: "pickup_date", order: "DESC" }}
    title="Loads"
  >
    <FreightTabs />
    <DataTable rowClick="edit">
      <DataTable.Col source="load_number" label="Load #" />
      <DataTable.Col label="Customer">
        <CompanyField />
      </DataTable.Col>
      <DataTable.Col label="Lane">
        <LaneField />
      </DataTable.Col>
      <DataTable.Col
        label="Carrier"
        render={(r: any) =>
          `${r.carrier_name ?? ""}${r.carrier_mc ? ` (MC ${r.carrier_mc})` : ""}`
        }
      />
      <DataTable.Col label="Pickup">
        <DateField source="pickup_date" />
      </DataTable.Col>
      <DataTable.Col label="Delivery">
        <DateField source="delivery_date" />
      </DataTable.Col>
      <DataTable.Col label="Customer $">
        <NumberField source="customer_rate" options={money} />
      </DataTable.Col>
      <DataTable.Col label="Carrier $">
        <NumberField source="carrier_rate" options={money} />
      </DataTable.Col>
      <DataTable.Col label="Margin">
        <NumberField source="margin" options={money} />
      </DataTable.Col>
      <DataTable.Col label="Status">
        <SelectField source="status" choices={loadStatuses} />
      </DataTable.Col>
      <DataTable.Col label="Agent">
        <OwnerField />
      </DataTable.Col>
    </DataTable>
  </List>
);
export const LoadCreate = () => (
  <Create redirect="list" title="New load" transform={transform}>
    <LoadForm />
  </Create>
);
export const LoadEdit = () => (
  <Edit redirect="list" title="Edit load" transform={transform}>
    <LoadForm />
  </Edit>
);
export default { list: LoadList, create: LoadCreate, edit: LoadEdit };
